import type { DateRange, DayPickerRangeProps, DayPickerSingleProps } from 'react-day-picker';

import dayjs from 'dayjs';
import { atom } from 'jotai';
import { atomWithImmer } from 'jotai-immer';

// Utils
import { DATETIME_FORMAT_SCHEMAS } from '@/utils/hooks/format/useFormatDateTime';

import type { UserType } from '@/types/userType';

// Types
import type { DatepickerPeriodsAvailableType } from '@/lib/periods';

type DatepickerSettingsType = {
    settings: {
        lang: UserType['settings']['platform_language'];
        dateFormat: (typeof DATETIME_FORMAT_SCHEMAS)[UserType['settings']['date_format']];
        timezone: UserType['settings']['timezone'];
        minDate?: string;
        maxDate?: string;
    };
};

export type DatepickerRangeType<TDate = DateRange, TPeriod = DatepickerPeriodsAvailableType> = {
    date: TDate;
    period: TPeriod;
    mode: 'range';
    onChange?: (data: { date: Required<TDate>; period: TPeriod }) => void;
    dayPickerOptions?: Omit<DayPickerRangeProps, 'selected' | 'mode' | 'fromDate' | 'toDate'>;
} & DatepickerSettingsType;

export type DatepickerSingleType<TDate = Date> = {
    date: TDate;
    mode: 'single';
    onChange?: (data: { date: TDate }) => void;
    dayPickerOptions?: Omit<DayPickerSingleProps, 'selected' | 'mode' | 'fromDate' | 'toDate'>;
} & DatepickerSettingsType;

export type DatepickerAtomType = DatepickerRangeType | DatepickerSingleType;
export type DatepickerRangeAtomImmer = ReturnType<typeof atomWithImmer<DatepickerRangeType>>;
export type DatepickerSingleAtomImmer = ReturnType<typeof atomWithImmer<DatepickerSingleType>>;

export const datepickerAtom = atomWithImmer<DatepickerAtomType>({
    date: null,
    mode: null,
    settings: {
        lang: null,
        timezone: null,
        dateFormat: null
    },
    dayPickerOptions: {}
});

export const datepickerInputValueAtom = atom((get) => {
    const datepicker = get(datepickerAtom);
    const formatDate = (date: Date) => {
        return dayjs(date).format(datepicker.settings.dateFormat);
    };

    if (datepicker.mode === 'range') {
        const dateFromFormatted = formatDate(datepicker.date.from);
        const dateToFormatted = formatDate(datepicker.date.to);
        if (datepicker.date?.to && dateFromFormatted !== dateToFormatted) {
            return `${dateFromFormatted} - ${dateToFormatted}`;
        }

        return dateFromFormatted;
    }

    return formatDate(datepicker.date);
});
